import { isPassable, terrainDef } from '../content/terrain';
import { octile } from '../lib/intmath';

/*
 * A* על רשת האריחים.
 *
 * שלוש דרישות שאינן "נחמד שיהיה":
 *   1. **דטרמיניזם מוחלט.** שני מחשבים שמריצים את אותו משחק חייבים לקבל את אותו
 *      מסלול, אחרת המצבים מתפצלים תוך שניות. לכן כל העלויות שלמות ושבירת השוויון
 *      בערמה היא לפי (f, h, אריח) — ולעולם לא לפי סדר הכנסה.
 *   2. **תקציב.** חיפוש לא חסום בקבוצה גדולה תוקע את הטיק. הקורא מגביל את מספר
 *      הצמתים; חיפוש שנגמר לו התקציב נכשל נקי ומחזיר null.
 *   3. **בלי חיתוך פינות.** מעבר באלכסון בין שני מכשולים נראה כמו יחידה שעוברת
 *      דרך קיר, וגם מייצר מסלולים שהתנועה בפועל לא יכולה לבצע.
 */

export const STRAIGHT_COST = 10;
export const DIAGONAL_COST = 14;

/**
 * תקרת צמתים לחיפוש בודד — **נגזרת מגודל הרשת ולא קבועה.**
 *
 * הייתה כאן קבוע 12,000 עם הערה "96×96 = 9,216, זה שפע". המפות החדשות שברו את
 * ההנחה בשקט: `crossroads` היא 128×128 = 16,384 אריחים, כלומר חיפוש שסורק את
 * רוב המפה נחתך, מחזיר `null`, והיחידה פשוט "לא זזה" — כולל גלי ההתקפה של
 * היריב. דטרמיניסטי, ולכן לא desync — אבל באג התנהגות שקשה לאתר.
 *
 * פי 1.5 מגודל הרשת: מכסה סריקה מלאה עם מרווח לצמתים שנדחפו שוב אחרי שיפור `g`.
 */
export function maxNodesFor(size: number): number {
    return Math.max(12_000, size + Math.floor(size / 2));
}

/** כמה בקשות מסלול נפתרות בטיק אחד. השאר ממתינות בתור. */
export const MAX_SEARCHES_PER_TICK = 8;

export interface Grid {
    readonly w: number;
    readonly h: number;
    readonly terrain: Uint8Array;
}

/** עלות כניסה לאריח, או -1 אם חסום. */
export function tileCost(grid: Grid, index: number): number {
    const id = grid.terrain[index] ?? 0;
    if (!isPassable(id)) return -1;
    const speed = terrainDef(id).speedPct;
    // ככל שהקרקע איטית יותר, המעבר בה יקר יותר. שלם תמיד.
    return Math.max(1, Math.round((STRAIGHT_COST * 100) / speed));
}

export function isWalkable(grid: Grid, x: number, y: number): boolean {
    if (x < 0 || x >= grid.w || y < 0 || y >= grid.h) return false;
    return isPassable(grid.terrain[y * grid.w + x] ?? 0);
}

/*
 * ערמה בינארית על מערכים מקבילים. אין כאן אובייקטים בכוונה: החיפוש רץ אלפי פעמים
 * בשנייה, והקצאת אובייקט לכל צומת הייתה מייצרת לחץ GC שנראה כמו גמגום בתנועה.
 */
class NodeHeap {
    private readonly tiles: Int32Array;
    private readonly f: Int32Array;
    private readonly h: Int32Array;
    private size = 0;

    constructor(capacity: number) {
        this.tiles = new Int32Array(capacity);
        this.f = new Int32Array(capacity);
        this.h = new Int32Array(capacity);
    }

    get length(): number {
        return this.size;
    }

    clear(): void {
        this.size = 0;
    }

    /** סדר מלא: f, ואז h, ואז מספר האריח. בלי השלישי אין דטרמיניזם. */
    private less(a: number, b: number): boolean {
        if (this.f[a] !== this.f[b]) return (this.f[a] ?? 0) < (this.f[b] ?? 0);
        if (this.h[a] !== this.h[b]) return (this.h[a] ?? 0) < (this.h[b] ?? 0);
        return (this.tiles[a] ?? 0) < (this.tiles[b] ?? 0);
    }

    private swap(a: number, b: number): void {
        const t = this.tiles[a] ?? 0;
        const f = this.f[a] ?? 0;
        const h = this.h[a] ?? 0;
        this.tiles[a] = this.tiles[b] ?? 0;
        this.f[a] = this.f[b] ?? 0;
        this.h[a] = this.h[b] ?? 0;
        this.tiles[b] = t;
        this.f[b] = f;
        this.h[b] = h;
    }

    push(tile: number, f: number, h: number): void {
        if (this.size >= this.tiles.length) return;
        let i = this.size++;
        this.tiles[i] = tile;
        this.f[i] = f;
        this.h[i] = h;
        while (i > 0) {
            const parent = (i - 1) >> 1;
            if (!this.less(i, parent)) break;
            this.swap(i, parent);
            i = parent;
        }
    }

    pop(): number {
        const top = this.tiles[0] ?? -1;
        this.size--;
        if (this.size > 0) {
            this.tiles[0] = this.tiles[this.size] ?? 0;
            this.f[0] = this.f[this.size] ?? 0;
            this.h[0] = this.h[this.size] ?? 0;
            let i = 0;
            for (;;) {
                const left = i * 2 + 1;
                const right = left + 1;
                let smallest = i;
                if (left < this.size && this.less(left, smallest)) smallest = left;
                if (right < this.size && this.less(right, smallest)) smallest = right;
                if (smallest === i) break;
                this.swap(i, smallest);
                i = smallest;
            }
        }
        return top;
    }
}

/*
 * מאגרי עבודה משותפים. החיפוש הוא סינכרוני ולא ריאנטרנטי, ולכן אפשר להחזיק מאגר
 * אחד ולנקות אותו בעזרת חותמת דור — במקום לאפס 9,216 תאים בכל קריאה.
 */
interface Workspace {
    readonly size: number;
    readonly gScore: Int32Array;
    readonly cameFrom: Int32Array;
    readonly stamp: Int32Array;
    readonly closed: Uint8Array;
    readonly heap: NodeHeap;
    generation: number;
}

let workspace: Workspace | null = null;

function getWorkspace(size: number): Workspace {
    if (!workspace || workspace.size !== size) {
        workspace = {
            size,
            gScore: new Int32Array(size),
            cameFrom: new Int32Array(size),
            stamp: new Int32Array(size),
            closed: new Uint8Array(size),
            /*
             * A\* בלי decrease-key דוחף צומת מחדש בכל שיפור `g`, ולכן מספר
             * הרשומות הפתוחות אינו חסום ב-`size`. ערמה בקיבולת `size + 1`
             * **מפילה דחיפות בשקט** כשהיא מתמלאת, והתוצאה היא "אין מסלול" שגוי.
             */
            heap: new NodeHeap(size * 2 + 1),
            generation: 0,
        };
    }
    return workspace;
}

const NEIGHBOURS: readonly (readonly [number, number, number])[] = [
    [1, 0, STRAIGHT_COST],
    [-1, 0, STRAIGHT_COST],
    [0, 1, STRAIGHT_COST],
    [0, -1, STRAIGHT_COST],
    [1, 1, DIAGONAL_COST],
    [1, -1, DIAGONAL_COST],
    [-1, 1, DIAGONAL_COST],
    [-1, -1, DIAGONAL_COST],
];

export interface PathResult {
    /** אריחים מהצעד הראשון ועד היעד, לא כולל אריח המוצא. */
    readonly tiles: number[];
    /** כמה צמתים נפתחו — לניטור התקציב. */
    readonly expanded: number;
}

/**
 * מחפש מסלול מ-(startX,startY) אל (goalX,goalY).
 * מחזיר null כשאין מסלול, כשהיעד חסום, או כשנגמר תקציב הצמתים.
 */
export function findPath(
    grid: Grid,
    startX: number,
    startY: number,
    goalX: number,
    goalY: number,
    maxNodes = maxNodesFor(grid.w * grid.h),
): PathResult | null {
    if (!isWalkable(grid, startX, startY) || !isWalkable(grid, goalX, goalY)) return null;

    const start = startY * grid.w + startX;
    const goal = goalY * grid.w + goalX;
    if (start === goal) return { tiles: [], expanded: 0 };

    const ws = getWorkspace(grid.w * grid.h);
    const generation = ++ws.generation;
    ws.heap.clear();

    ws.gScore[start] = 0;
    ws.cameFrom[start] = -1;
    ws.stamp[start] = generation;
    ws.closed[start] = 0;

    const heuristic = (tile: number) => {
        const x = tile % grid.w;
        const y = (tile - x) / grid.w;
        return octile(x - goalX, y - goalY, STRAIGHT_COST, DIAGONAL_COST);
    };

    ws.heap.push(start, heuristic(start), heuristic(start));

    let expanded = 0;

    while (ws.heap.length > 0) {
        const current = ws.heap.pop();
        if (ws.stamp[current] !== generation || ws.closed[current] === 1) continue;
        ws.closed[current] = 1;
        expanded++;

        if (current === goal) {
            const tiles: number[] = [];
            for (let node = goal; node !== start; node = ws.cameFrom[node] ?? start) {
                tiles.push(node);
                if (tiles.length > ws.size) return null; // הגנה מפני שרשרת שבורה
            }
            tiles.reverse();
            return { tiles, expanded };
        }

        if (expanded >= maxNodes) return null;

        const cx = current % grid.w;
        const cy = (current - cx) / grid.w;
        const currentG = ws.gScore[current] ?? 0;

        for (const [dx, dy, stepCost] of NEIGHBOURS) {
            const nx = cx + dx;
            const ny = cy + dy;
            if (!isWalkable(grid, nx, ny)) continue;

            // בלי חיתוך פינות: אלכסון מותר רק אם שני האריחים הצמודים פנויים.
            if (dx !== 0 && dy !== 0) {
                if (!isWalkable(grid, cx + dx, cy) || !isWalkable(grid, cx, cy + dy)) continue;
            }

            const neighbour = ny * grid.w + nx;
            if (ws.stamp[neighbour] === generation && ws.closed[neighbour] === 1) continue;

            const enter = tileCost(grid, neighbour);
            if (enter < 0) continue;
            // עלות הצעד משקללת גם את האלכסון וגם את איטיות הקרקע.
            const tentative = currentG + Math.round((stepCost * enter) / STRAIGHT_COST);

            const seen = ws.stamp[neighbour] === generation;
            if (seen && tentative >= (ws.gScore[neighbour] ?? 0)) continue;

            ws.stamp[neighbour] = generation;
            ws.closed[neighbour] = 0;
            ws.gScore[neighbour] = tentative;
            ws.cameFrom[neighbour] = current;
            const h = heuristic(neighbour);
            ws.heap.push(neighbour, tentative + h, h);
        }
    }

    return null;
}
